import uniqBy from "lodash/uniqBy";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect } from "react";

import { useFunnelContext } from "shared/Funnel/FunnelContextProvider";
import { TrustpilotWidget } from "shared/TrustpilotWidget";
import { useTrackProduct } from "shared/analytics/useTrackProduct";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { ProductImages } from "shared/product/ProductSection/ProductImages";
import { HorizontalSeparator } from "ui/HorizontalSeparator";
import { StrapiImageData } from "ui/StrapiImage";
import { useModalState } from "utils/useModalState";
import { useI18n } from "utils/with-i18n.utils";

import { AdvantageDetails } from "./AvailableProduct/AdvantageDetails";
import { StickySection } from "./AvailableProduct/StickySection";
import { WithProductReminderModal } from "./WithProductReminderModal";
import { PictureThumbnail } from "./shared/PictureThumbnail";
import { StorageCard } from "./shared/StorageCard";

export const ComingSoonProduct = () => {
  const i18n = useI18n();
  const { isOpen, closeModal, openModal } = useModalState();
  const { family, variants, currentVariant } = useProductPageContext();
  const { isSwap } = useFunnelContext();
  const { trackProductClicked, trackProductViewed } = useTrackProduct();
  const {
    query: { slug },
  } = useRouter();
  useEffect(() => {
    if (currentVariant?.sku) {
      trackProductViewed(currentVariant.sku);
    }
  }, [currentVariant, trackProductViewed]);

  const basePath = `${isSwap ? "/swap" : "/smartphone"}/${slug}`;
  const subscriptionId = currentVariant?.id;
  const availableColors = uniqBy(variants, "colorSlug");
  const availableStorages = uniqBy(variants, "storageSlug");

  if (!subscriptionId) {
    return null;
  }

  return (
    <WithProductReminderModal
      isOpen={isOpen}
      closeModal={closeModal}
      productName={family.modelName!}
      subscriptionId={subscriptionId}
    >
      <div className="flex flex-col gap-4 items-center py-5 px-8 lg:items-start lg:py-8 lg:w-full">
        {/* Title */}
        <h1 className="mt-12 text-2xl text-center lg:mt-0 lg:text-left xs:text-3xl">
          {i18n.t(
            "product.comingSoon.bookNow",
            { productName: family.modelName },
            { html: true },
          )}
        </h1>

        {/* Trustpilot */}
        <TrustpilotWidget className="mb-5 lg:-ml-4 lg:w-[420px]" />

        {/* Mobile carousel */}
        <ProductImages className="-mt-5 w-screen min-w-320 lg:hidden" />
        <HorizontalSeparator className="w-screen min-w-320 lg:hidden" />

        {/* Color selector */}
        <p className="text-black">{i18n.t("product.pickColor")}</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {availableColors?.map(({ id, sku, colorSlug, backPicture }) => (
            <Link
              key={id}
              href={`${basePath}/${currentVariant?.storageSlug}-${colorSlug}`}
              onClick={() => trackProductClicked(sku!)}
              legacyBehavior={false}
              replace
              shallow
            >
              <PictureThumbnail
                image={backPicture as StrapiImageData}
                isSelected={colorSlug === currentVariant?.colorSlug}
              />
            </Link>
          ))}
        </div>

        {/* Storage selector */}
        <p className="text-black">{i18n.t("product.pickStorage")}</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {availableStorages.map(
            ({ id, sku, storage, storageSlug: variantStorageSlug }) => (
              <Link
                key={id}
                href={`${basePath}/${variantStorageSlug}-${currentVariant?.colorSlug}`}
                onClick={() => trackProductClicked(sku!)}
                legacyBehavior={false}
                replace
                shallow
              >
                <StorageCard
                  label={storage ?? ""}
                  isSelected={
                    variantStorageSlug === currentVariant?.storageSlug
                  }
                />
              </Link>
            ),
          )}
        </div>

        {/* Conditions */}
        <AdvantageDetails
          currentVariant={currentVariant}
          freeChangeAfterMonths={10}
        />

        {/* Sticky section for mobile */}
        <StickySection className="fixed bottom-0 w-screen min-w-320 lg:hidden" />
      </div>
    </WithProductReminderModal>
  );
};
